import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "../../../shared/components/Backbtn";
import {
  TextField,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { PhotoCamera, VideoCameraBack, Close } from "@mui/icons-material";
import { db, firebaseAuth } from "../../../context/Firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../../context/Firebase";

const EditPost = () => {
  const { postId } = useParams();
  const [postText, setPostText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPost, setLoadingPost] = useState(true);
  const [isVideo, setIsVideo] = useState(false);
  const [detectedHashtags, setDetectedHashtags] = useState([]);
  const navigate = useNavigate();
  const [authUser] = useAuthState(firebaseAuth);

  // Extract hashtags from text
  useEffect(() => {
    const hashtagRegex = /#(\w+)/g;
    const matches = postText.match(hashtagRegex);
    setDetectedHashtags(matches || []);
  }, [postText]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const postRef = doc(db, "posts", postId);
        const postSnap = await getDoc(postRef);
        
        if (postSnap.exists()) {
          const postData = postSnap.data();
          
          // Check if user owns this post
          if (postData.userId !== authUser?.uid) {
            alert("You don't have permission to edit this post");
            navigate("/post");
            return;
          }

          setPostText(postData.content || postData.postText || "");
          setIsVideo(postData.isVideo || false);
          
          // Handle existing images
          const images = [];
          if (postData.image) images.push(postData.image);
          if (postData.image1) images.push(postData.image1);
          if (postData.image2) images.push(postData.image2);
          if (postData.image3) images.push(postData.image3);
          if (postData.image4) images.push(postData.image4);
          
          setExistingImages(images.filter(Boolean));
        } else {
          alert("Post not found");
          navigate("/post");
        }
      } catch (err) {
        console.error("Error fetching post:", err);
        alert("Failed to load post");
        navigate("/post");
      } finally {
        setLoadingPost(false);
      }
    };

    if (postId && authUser) {
      fetchPost();
    }
  }, [postId, authUser, navigate]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles(files);
      setIsVideo(false);
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFiles([file]);
      setIsVideo(true);
    }
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeNewFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const isPostTextPresent = postText.trim() !== '';
    const hasExistingImages = existingImages.length > 0;
    const hasNewFiles = selectedFiles.length > 0;

    if (!isPostTextPresent && !hasExistingImages && !hasNewFiles) {
      alert("Please add a post message or image.");
      return;
    }

    setIsLoading(true);

    try {
      const postRef = doc(db, "posts", postId);
      const updateData = {
        content: postText.trim(),
        postText: postText.trim(),
        isVideo: isVideo,
      };

      // Upload new files if any
      if (selectedFiles.length > 0) {
        const uploadedUrls = [];
        for (const file of selectedFiles) {
          const storageRef = ref(storage, `posts/${Date.now()}_${file.name}`);
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          uploadedUrls.push(url);
        }
        
        // Combine existing and new images
        const allImages = [...existingImages, ...uploadedUrls];
        updateData.image = allImages[0] || "";
        updateData.image1 = allImages[1] || "";
        updateData.image2 = allImages[2] || "";
        updateData.image3 = allImages[3] || "";
        updateData.image4 = allImages[4] || "";
      } else {
        // Keep only existing images
        updateData.image = existingImages[0] || "";
        updateData.image1 = existingImages[1] || "";
        updateData.image2 = existingImages[2] || "";
        updateData.image3 = existingImages[3] || "";
        updateData.image4 = existingImages[4] || "";
      }

      await updateDoc(postRef, updateData);

      alert("Post updated successfully!");
      navigate(`/post/${postId}`);
    } catch (err) {
      console.error("Update error:", err);
      alert("Failed to update post. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (loadingPost) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center min-h-screen p-5 bg-gray-50">
      <div className="w-full flex justify-end">
        <BackButton tooltip="Back" />
      </div>

      <h2 className="text-2xl font-bold mb-4">Edit Post</h2>

      <TextField
        multiline
        rows={4}
        placeholder="What's on your mind? Use #hashtags to categorize your post"
        value={postText}
        onChange={(e) => setPostText(e.target.value)}
        fullWidth
        variant="outlined"
        margin="normal"
        className="w-full max-w-2xl bg-white rounded-lg shadow-sm"
      />

      {/* Display detected hashtags */}
      {detectedHashtags.length > 0 && (
        <div className="w-full max-w-2xl p-3 rounded-lg" style={{ backgroundColor: 'var(--app-active-bg)' }}>
          <p className="text-sm text-gray-600 mb-2">Detected hashtags:</p>
          <div className="flex flex-wrap gap-2">
            {detectedHashtags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 text-white rounded-full text-sm"
                style={{ backgroundColor: 'var(--app-accent)' }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Existing Images */}
      {existingImages.length > 0 && (
        <div className="w-full max-w-2xl mb-3">
          <p className="text-sm text-gray-600 mb-2">Current images:</p>
          <div className="flex flex-wrap gap-3">
            {existingImages.map((url, index) => (
              <div key={index} className="relative">
                {isVideo ? (
                  <video
                    src={url}
                    controls
                    className="object-cover w-24 h-24 rounded-lg shadow-md"
                  />
                ) : (
                  <img
                    src={url}
                    alt={`Existing ${index + 1}`}
                    className="object-cover w-24 h-24 rounded-lg shadow-md"
                  />
                )}
                <IconButton
                  size="small"
                  onClick={() => removeExistingImage(index)}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'error.dark' },
                    width: 24,
                    height: 24
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Files Preview */}
      {selectedFiles.length > 0 && (
        <div className="w-full max-w-2xl mb-3">
          <p className="text-sm text-gray-600 mb-2">New files to upload:</p>
          <div className="flex flex-wrap gap-3">
            {selectedFiles.map((file, index) => (
              <div key={index} className="relative">
                {isVideo ? (
                  <video
                    src={URL.createObjectURL(file)}
                    controls
                    className="object-cover w-24 h-24 rounded-lg shadow-md"
                  />
                ) : (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Preview ${index + 1}`}
                    className="object-cover w-24 h-24 rounded-lg shadow-md"
                  />
                )}
                <IconButton
                  size="small"
                  onClick={() => removeNewFile(index)}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'error.dark' },
                    width: 24,
                    height: 24
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-4 mb-5">
        <input
          accept="image/png,image/jpeg,image/jpg"
          id="image-upload"
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <label htmlFor="image-upload">
          <IconButton component="span" sx={{ color: 'white', bgcolor: 'var(--app-accent)', '&:hover': { bgcolor: 'var(--app-accent-hover)' } }}>
            <PhotoCamera />
          </IconButton>
        </label>

        <input
          accept="video/mp4,video/webm"
          id="video-upload"
          type="file"
          style={{ display: "none" }}
          onChange={handleVideoChange}
        />
        <label htmlFor="video-upload">
          <IconButton component="span" sx={{ color: 'white', bgcolor: 'var(--app-accent)', '&:hover': { bgcolor: 'var(--app-accent-hover)' } }}>
            <VideoCameraBack />
          </IconButton>
        </label>
      </div>

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={isLoading}
        className="w-full max-w-2xl px-4 py-2 font-bold text-white rounded-lg shadow-md"
      >
        {isLoading ? <CircularProgress size={24} className="text-white" /> : "Update Post"}
      </Button>
    </div>
  );
};

export default EditPost;
